import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../components/CustomButton/CustomButton";
import { CustomTextSelect } from "../../../components/CustomTextSelect/CustomTextSelect";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { useGetOneCompanie, useUpdateCompanyVat } from "../../../api/companies";
import { getCurrentFleetId, isFleetAdmin } from "../../../utils/getCurrentFleet";
import useStyles from "./styles";

// Standard French VAT rates. The current company rate is always merged in so a
// non-standard saved value is never lost from the dropdown.
const STANDARD_VAT_RATES = [20, 10, 5.5, 2.1, 0];

export const ContainerTVA = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const companyId = getCurrentFleetId();
  const canEdit = isFleetAdmin();
  const isMobile = useMediaQuery("(max-width:599.95px)");

  const { companie, isLoading } = useGetOneCompanie(companyId);
  const { mutate: updateVat, isPending } = useUpdateCompanyVat(companyId);

  const [selectedVat, setSelectedVat] = useState<number | "">("");

  useEffect(() => {
    if (companie?.vat !== undefined && companie?.vat !== null) {
      setSelectedVat(Number(companie.vat));
    }
  }, [companie?.vat]);

  const options = useMemo(() => {
    const rates = new Set<number>(STANDARD_VAT_RATES);
    if (typeof selectedVat === "number") rates.add(selectedVat);
    return Array.from(rates)
      .sort((a, b) => b - a)
      .map((rate) => ({ label: `${rate}%`, value: rate }));
  }, [selectedVat]);

  const currentVat =
    companie?.vat !== undefined && companie?.vat !== null
      ? Number(companie.vat)
      : null;
  const isDirty = selectedVat !== "" && selectedVat !== currentVat;

  const onApply = () => {
    if (typeof selectedVat !== "number") return;
    updateVat({ vat: selectedVat });
  };

  return (
    <div className={classes.divContainerTVA}>
      <CustomTypography
        text={t("setting.companyVAT", "company VAT")}
        variant={"h3"}
        isUppercase
      />
      <div className={classes.divButtonTVA}>
        <CustomTextSelect
          label={t("setting.selectTheVATrate", "Select the VAT rate")}
          placeholder={t("setting.selectTheVATrate", "Select the VAT rate")}
          options={options}
          value={selectedVat}
          disabled={!canEdit || isLoading || isPending}
          onChange={(event) => setSelectedVat(Number(event.target.value))}
        />
        <CustomButton
          label={t("common.apply", "Apply")}
          variant={canEdit && isDirty && !isPending ? "primary" : "disabled"}
          disabled={!canEdit || !isDirty || isPending}
          loading={isPending}
          width={isMobile ? "100%" : "fit-content"}
          height={"40px"}
          onClick={onApply}
        />
      </div>
      {!canEdit && (
        <CustomTypography
          text={t(
            "setting.readOnlyHint",
            "Only a fleet administrator can change this setting."
          )}
          variant={"link"}
          color={"var(--c-muted)"}
        />
      )}
    </div>
  );
};
